<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAdminRolesStore, useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import type { Role, AccountRole } from '@/types/rbac'

const props = defineProps<{
  accountId: number
}>()

const { t, d } = useI18n()
const adminRolesStore = useAdminRolesStore()
const notifyStore = useNotifyStore()

const loading = ref(true)
const accountRoles = ref<AccountRole[]>([])
const allRoles = ref<Role[]>([])
const showAssignDialog = ref(false)
const selectedRoleId = ref<number | null>(null)
const expiryDate = ref<string | null>(null)
const noExpiry = ref(true)
const confirmRevokeId = ref<number | null>(null)

const showRevokeDialog = computed({
  get: () => confirmRevokeId.value !== null,
  set: (val: boolean) => {
    if (!val) confirmRevokeId.value = null
  }
})

const availableRoles = computed(() => {
  const assignedIds = new Set(accountRoles.value.map((r) => r.role_id))
  return allRoles.value.filter((r) => !assignedIds.has(r.id))
})

const formatDate = (dateStr: string): string => {
  try {
    return d(new Date(dateStr), 'short')
  } catch {
    return dateStr
  }
}

const fetchData = async () => {
  loading.value = true
  const [roles, userRoles] = await Promise.all([
    adminRolesStore.fetchRoles(),
    adminRolesStore.fetchAccountRoles(props.accountId)
  ])
  allRoles.value = roles
  accountRoles.value = userRoles
  loading.value = false
}

const handleAssign = async () => {
  if (!selectedRoleId.value) return

  const success = await adminRolesStore.assignRole(
    props.accountId,
    selectedRoleId.value,
    noExpiry.value ? undefined : expiryDate.value ?? undefined
  )

  if (success) {
    const role = allRoles.value.find((r) => r.id === selectedRoleId.value)
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.admin.roles.userRoles.notifications.assigned'),
      message: t('dashboard.admin.roles.userRoles.notifications.assignedMessage', {
        role: role?.display_name ?? ''
      })
    })
    showAssignDialog.value = false
    selectedRoleId.value = null
    expiryDate.value = null
    noExpiry.value = true
    await fetchData()
  }
}

const handleRevoke = async (roleId: number) => {
  const role = accountRoles.value.find((r) => r.role_id === roleId)
  const success = await adminRolesStore.revokeRole(props.accountId, roleId)

  if (success) {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.admin.roles.userRoles.notifications.revoked'),
      message: t('dashboard.admin.roles.userRoles.notifications.revokedMessage', {
        role: role?.display_name ?? ''
      })
    })
    confirmRevokeId.value = null
    await fetchData()
  }
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <div class="user-roles-section">
    <!-- Section Header -->
    <div class="section-header">
      <div class="header-content">
        <div class="header-icon">
          <v-icon icon="mdi-shield-account" size="20" />
        </div>
        <div class="header-text">
          <h3>{{ $t('dashboard.admin.roles.userRoles.title') }}</h3>
          <p>{{ $t('dashboard.admin.roles.userRoles.subtitle') }}</p>
        </div>
      </div>
      <button
        v-if="availableRoles.length > 0"
        class="assign-btn"
        @click="showAssignDialog = true"
      >
        <v-icon icon="mdi-plus" size="18" />
        <span>{{ $t('dashboard.admin.roles.userRoles.assignRole') }}</span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <!-- Empty State -->
    <div v-else-if="accountRoles.length === 0" class="empty-state">
      <v-icon icon="mdi-shield-off-outline" size="32" />
      <span class="empty-title">{{ $t('dashboard.admin.roles.userRoles.noRoles') }}</span>
      <span class="empty-description">{{ $t('dashboard.admin.roles.userRoles.noRolesDescription') }}</span>
    </div>

    <!-- Roles List -->
    <div v-else class="roles-list">
      <div v-for="role in accountRoles" :key="role.role_id" class="role-card">
        <div class="role-info">
          <div class="role-name">
            <v-icon icon="mdi-badge-account" size="18" />
            <span>{{ role.display_name }}</span>
          </div>
          <div v-if="role.description" class="role-description">{{ role.description }}</div>
          <div class="role-meta">
            <span class="meta-item">
              <v-icon icon="mdi-calendar-check" size="14" />
              {{ $t('dashboard.admin.roles.userRoles.grantedAt') }}: {{ formatDate(role.granted_at) }}
            </span>
            <span v-if="role.expires_at" class="meta-item expires">
              <v-icon icon="mdi-calendar-clock" size="14" />
              {{ $t('dashboard.admin.roles.userRoles.expiresAt') }}: {{ formatDate(role.expires_at) }}
            </span>
          </div>
        </div>
        <div class="role-actions">
          <button
            class="revoke-btn"
            @click="confirmRevokeId = role.role_id"
          >
            <v-icon icon="mdi-close" size="16" />
          </button>
        </div>
      </div>
    </div>

    <!-- Assign Dialog -->
    <v-dialog v-model="showAssignDialog" max-width="420" persistent>
      <div class="dialog-card">
        <div class="dialog-header">
          <div class="dialog-icon assign">
            <v-icon icon="mdi-shield-plus" size="24" />
          </div>
          <h3 class="dialog-title">{{ $t('dashboard.admin.roles.userRoles.assignRole') }}</h3>
        </div>

        <div class="dialog-body">
          <div class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.roles.userRoles.selectRole') }}</label>
            <v-select
              v-model="selectedRoleId"
              :items="availableRoles"
              item-title="display_name"
              item-value="id"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>

          <div class="dialog-field">
            <label class="checkbox-label" @click="noExpiry = !noExpiry">
              <span class="checkbox-box" :class="{ checked: noExpiry }">
                <v-icon v-if="noExpiry" icon="mdi-check" size="14" />
              </span>
              <span>{{ $t('dashboard.admin.roles.userRoles.noExpiry') }}</span>
            </label>
            <input
              v-if="!noExpiry"
              v-model="expiryDate"
              type="date"
              class="date-input"
            />
          </div>
        </div>

        <div class="dialog-actions">
          <button class="dialog-btn secondary" @click="showAssignDialog = false">
            {{ $t('common.cancel') }}
          </button>
          <button
            class="dialog-btn primary"
            :disabled="!selectedRoleId"
            @click="handleAssign"
          >
            <v-icon icon="mdi-shield-plus" size="16" />
            <span>{{ $t('dashboard.admin.roles.userRoles.assignRole') }}</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Revoke Confirmation Dialog -->
    <v-dialog v-model="showRevokeDialog" max-width="400" persistent>
      <div class="dialog-card">
        <div class="dialog-header">
          <div class="dialog-icon revoke">
            <v-icon icon="mdi-shield-remove" size="24" />
          </div>
          <h3 class="dialog-title">{{ $t('dashboard.admin.roles.userRoles.revokeRole') }}</h3>
          <p class="dialog-subtitle">
            {{ $t('dashboard.admin.roles.userRoles.confirmRevoke', {
              role: accountRoles.find(r => r.role_id === confirmRevokeId)?.display_name ?? ''
            }) }}
          </p>
        </div>

        <div class="dialog-actions">
          <button class="dialog-btn secondary" @click="confirmRevokeId = null">
            {{ $t('common.cancel') }}
          </button>
          <button
            class="dialog-btn danger"
            @click="handleRevoke(confirmRevokeId!)"
          >
            <v-icon icon="mdi-shield-remove" size="16" />
            <span>{{ $t('dashboard.admin.roles.userRoles.revokeRole') }}</span>
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
.user-roles-section {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(103, 58, 183, 0.12);
  border: 1px solid rgba(103, 58, 183, 0.25);
  border-radius: 8px;
  color: #b39ddb;
  flex-shrink: 0;
}

.header-text {
  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  p {
    margin: 2px 0 0;
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.5);
  }
}

.assign-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(103, 58, 183, 0.1);
  border: 1px solid rgba(103, 58, 183, 0.3);
  border-radius: 8px;
  color: #b39ddb;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(103, 58, 183, 0.2);
  }
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  color: rgba(200, 218, 255, 0.4);

  .empty-title {
    font-weight: 500;
    color: rgba(200, 218, 255, 0.6);
  }

  .empty-description {
    font-size: 0.85rem;
  }
}

.roles-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.role-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(103, 58, 183, 0.05);
  border: 1px solid rgba(103, 58, 183, 0.15);
  border-radius: 8px;
}

.role-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.role-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.role-description {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
  padding-left: 26px;
}

.role-meta {
  display: flex;
  gap: 12px;
  padding-left: 26px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  color: rgba(200, 218, 255, 0.5);

  &.expires {
    color: rgba(255, 183, 77, 0.8);
  }
}

.revoke-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 6px;
  color: #ef5350;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.2);
    border-color: rgba(244, 67, 54, 0.3);
  }
}

.dialog-card {
  background: rgb(20, 24, 36);
  border: 1px solid rgba(120, 163, 255, 0.12);
  border-radius: 16px;
  padding: 28px 24px 20px;
}

.dialog-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  margin-bottom: 24px;
}

.dialog-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;

  &.assign {
    background: rgba(103, 58, 183, 0.12);
    border: 1px solid rgba(103, 58, 183, 0.25);
    color: #b39ddb;
  }

  &.revoke {
    background: rgba(244, 67, 54, 0.12);
    border: 1px solid rgba(244, 67, 54, 0.25);
    color: #ef5350;
  }
}

.dialog-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.dialog-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  line-height: 1.4;
}

.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.dialog-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dialog-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.8);
}

.checkbox-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 1.5px solid rgba(120, 163, 255, 0.25);
  background: rgba(255, 255, 255, 0.03);
  color: transparent;
  transition: all 0.15s ease;

  &.checked {
    background: rgba(103, 58, 183, 0.3);
    border-color: rgba(103, 58, 183, 0.6);
    color: #b39ddb;
  }
}

.date-input {
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(103, 58, 183, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.dialog-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background: rgba(103, 58, 183, 0.15);
    border: 1px solid rgba(103, 58, 183, 0.35);
    color: #b39ddb;

    &:hover:not(:disabled) {
      background: rgba(103, 58, 183, 0.25);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &.danger {
    background: rgba(244, 67, 54, 0.15);
    border: 1px solid rgba(244, 67, 54, 0.35);
    color: #ef5350;

    &:hover {
      background: rgba(244, 67, 54, 0.25);
    }
  }

  &.secondary {
    background: transparent;
    border: 1px solid rgba(120, 163, 255, 0.15);
    color: rgba(200, 218, 255, 0.6);

    &:hover {
      background: rgba(120, 163, 255, 0.05);
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .user-roles-section {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .header-text h3 {
    color: rgba(0, 0, 0, 0.87);
  }

  .header-text p {
    color: rgba(0, 0, 0, 0.54);
  }

  .empty-state {
    color: rgba(0, 0, 0, 0.38);

    .empty-title {
      color: rgba(0, 0, 0, 0.6);
    }
  }

  .role-card {
    background: rgba(103, 58, 183, 0.04);
    border-color: rgba(103, 58, 183, 0.12);
  }

  .role-name {
    color: rgba(0, 0, 0, 0.87);
  }

  .role-description {
    color: rgba(0, 0, 0, 0.6);
  }

  .meta-item {
    color: rgba(0, 0, 0, 0.54);
  }

  .dialog-card {
    background: #fff;
    border-color: rgba(0, 0, 0, 0.08);
  }

  .dialog-title {
    color: rgba(0, 0, 0, 0.87);
  }

  .dialog-subtitle {
    color: rgba(0, 0, 0, 0.6);
  }

  .dialog-label,
  .checkbox-label {
    color: rgba(0, 0, 0, 0.7);
  }

  .date-input {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(0, 0, 0, 0.15);
    color: rgba(0, 0, 0, 0.87);
  }
}
</style>
